//changed due to package upgrade
import AppCard from '@projectK/components/AppCard';
import React, { useState, useEffect } from 'react';
import {
  Autocomplete,
  Button,
  Grid,
  IconButton,
  MenuItem,
  Stack,
  TextField,
  alpha,
} from '@mui/material';
import AppGridContainer from '@projectK/components/AppGridContainer';
import PropTypes from 'prop-types';
import { ExpandLessOutlined, ExpandMoreOutlined } from '@mui/icons-material';
import jwtAxios from '@projectK/services/auth/JWT';
import {
  DatePicker,
  LocalizationProvider,
  TimePicker,
} from '@mui/x-date-pickers';
import dayjs from 'dayjs';
import CompanyTheme from '@projectK/constants/CompanyTheme';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { FilterModal } from '@projectK/ReUseFunctions';

const FeedbackFilter = ({
  productFilter,
  setProductFilter,
  activityFilter,
  setActivityFilter,
  dateFilter,
  setDateFilter,
  timeFilter,
  setTimeFilter,
  yearFilter,
  setYearFilter,
  years,
  setIsFilterOpen,
  setPage,
  isMobile,
  openFilterModal,
  handleFilterModalClose,
}) => {
  const [isExpandFilter, setIsExpandOpen] = useState(true);
  const [productsOptions, setProductOptions] = useState([]);
  const [activitiesOptions, setActivitiesOptions] = useState([]);
  const [searchTermActivity, setSearchTermActivity] = useState('');
  const [searchTermProduct, setSearchTermProduct] = useState('');
  const verbiageData = JSON.parse(localStorage.getItem('verbiageSettings')) || {};
  const companyTheme = CompanyTheme();
  const handleFilterChange = (filterType, value) => {
    if (filterType === 'productId') {
      setProductFilter(value ? value.id : '');
      setPage(0);
    }
    if (filterType === 'activity_id') {
      setActivityFilter(value ? value.activity_name : '');
      setPage(0);
    }
  };

  const handleDateFilter = (selectedValue) => {
    setDateFilter(selectedValue);
    setPage(0);
  };

  const handleTimeFilter = (selectedValue) => {
    setTimeFilter(selectedValue);
    setPage(0);
  };
  const handleYearChange = (value) => {
    setYearFilter(value);
    setPage(0);
  };

  const stopPropagation = (e) => {
    e.stopPropagation();
  };

  const toggleFilter = () => {
    setIsExpandOpen(!isExpandFilter);
  };

  const clearFilter = () => {
    setProductFilter('');
    setActivityFilter('');
    setDateFilter('');
    setTimeFilter('');
    setYearFilter(null);
    setIsFilterOpen(true);
    setPage(0);
  };
  const fetchProductData = async () => {
    try {
      const response = await jwtAxios.get(`/products`, {
        params: {
          perPage: 'all',
        },
      });
      const { data } = response;
      setProductOptions(data.list);
    } catch (error) {
      setProductOptions([]);
    }
  };
  useEffect(() => {
    fetchProductData();
  }, []);

  const fetchActivitiesData = async () => {
    try {
      const response = await jwtAxios.get(`/activities`, {
        params: {
          perPage: 'all',
        },
      });
      const { data } = response;
      const activitySet = new Set();

      // Iterate through the activities to populate the Set
      data.list.forEach((activity) => {
        if (
          activity.activity_name !== null &&
          activity.activity_display_name !== null
        ) {
          activitySet.add(
            JSON.stringify({
              activity_name: activity.activity_name,
              activity_display_name: activity.activity_display_name,
            }),
          );
        }
      });

      // Convert the Set back to an array of objects with both properties
      const activityNames = Array.from(activitySet).map((entry) =>
        JSON.parse(entry),
      );
      setActivitiesOptions(activityNames);
    } catch (error) {
      setActivitiesOptions([]);
    }
  };
  useEffect(() => {
    fetchActivitiesData();
  }, []);

  var filterDate = new Date(`${dateFilter?.substring(0, 10)}T12:01:00`);

  const modalContent = (
    <AppGridContainer
      spacing={5}
      onClick={stopPropagation}
      sx={{ marginTop: '2px' }}
    >
      <Grid  size={{ md: 4, sm: 6,xs:12  }}>
        <TextField
          label='Year'
          select
          value={yearFilter || ''}
          onChange={(e) => handleYearChange(e.target.value)}
          sx={{
            width: '100%',
          }}
        >
          {years.map((year) => (
            <MenuItem key={year} value={year}>
              {year}
            </MenuItem>
          ))}
        </TextField>
      </Grid>
      <Grid  size={{ md: 4, sm: 6,xs:12  }}>
        <Autocomplete
          options={(productsOptions || []).sort((a, b) =>
            a.product_name.localeCompare(b.product_name, undefined, {
              numeric: true,
              sensitivity: 'base',
            }),
          )}
          getOptionLabel={(option) =>
            option.badge
              ? `${option.product_name} - ${option.badge}`
              : option.product_name
          }
          value={productsOptions.find((p) => p.id === productFilter) || null}
          onChange={(_, value) => handleFilterChange('productId', value || {})}
          inputValue={searchTermProduct}
          onInputChange={(_, value) => setSearchTermProduct(value)}
          renderInput={(params) => (
            <TextField {...params} label={verbiageData?.product_type || 'Products'} variant='outlined' />
          )}
        />
      </Grid>
      <Grid  size={{ md: 4, sm: 6,xs:12  }}>
        <Autocomplete
          options={(activitiesOptions || []).sort((a, b) =>
            a.activity_name.localeCompare(b.activity_name, undefined, {
              numeric: true,
              sensitivity: 'base',
            }),
          )}
          getOptionLabel={(option) =>
            `${option.activity_name} - ${option.activity_display_name}`
          }
          value={
            activitiesOptions.find(
              (p) =>
                p.activity_name === activityFilter ||
                p.activity_display_name === activityFilter,
            ) || null
          }
          onChange={(_, value) =>
            handleFilterChange('activity_id', value || {})
          }
          inputValue={searchTermActivity}
          onInputChange={(_, value) => setSearchTermActivity(value)}
          renderInput={(params) => (
            <TextField {...params} label='Activities' variant='outlined' />
          )}
        />
      </Grid>
      <Grid  size={{ md: 4, sm: 6,xs:12  }}>
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <Stack spacing={1}>
            <DatePicker
              label='Start Date'
              sx={{
                width: '100%',
                backgroundColor: 'background.paper',
                color: 'text.primary',
              }}
              value={dateFilter ? filterDate : null}
              renderInput={(params) => <TextField {...params} />}
              onChange={(value) => {
                const formattedDate = dayjs(value)
                  .startOf('day')
                  .format('YYYY-MM-DD');

                handleDateFilter(
                  formattedDate === 'Invalid Date' ? '' : formattedDate,
                );
              }}
            />
          </Stack>
        </LocalizationProvider>
      </Grid>
      <Grid  size={{ md: 4, sm: 6,xs:12  }}>
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <Stack spacing={1}>
            <TimePicker
              label='Start Time'
              sx={{
                width: '100%',
                backgroundColor: 'background.paper',
                color: 'text.primary',
              }}
              value={timeFilter ? dayjs(timeFilter, 'HH:mm').toDate() : null}
              renderInput={(params) => <TextField {...params} />}
              onChange={(value) => {
                const formattedTime = dayjs(value).format('HH:mm');
                handleTimeFilter(formattedTime);
              }}
            />
          </Stack>
        </LocalizationProvider>
      </Grid>
      {!isMobile && (
        <Grid  size={{ md: 4, sm: 6,xs:12  }}>
        <Button
            variant='outlined'
            sx={{
              borderColor: companyTheme ? companyTheme.primary.main : '#2196f3',
              '&:hover': {
                borderColor: companyTheme
                  ? alpha(companyTheme.primary.main, 0.8)
                  : alpha('#2196f3', 0.8),
              },
              color: companyTheme ? companyTheme.primary.main : '#2196f3',
            }}
            onClick={clearFilter}
          >
            Clear Filter
          </Button>
        </Grid>
      )}
    </AppGridContainer>
  );
  return isMobile ? (
    <FilterModal
      modalContent={modalContent}
      isOpen={openFilterModal}
      onClose={handleFilterModalClose}
      clearFilter={clearFilter}
      companyTheme={companyTheme}
      title={'Filter Feedback'}
    />
  ) : (
    <AppCard
      title='Filter Feedback'
      style={{ position: 'relative' }}
      onClick={toggleFilter}
      action={
        <IconButton
          onClick={toggleFilter}
          size='small'
          style={{
            position: 'absolute',
            top: '0',
            right: '0',
          }}
        >
          {isExpandFilter ? <ExpandLessOutlined /> : <ExpandMoreOutlined />}
        </IconButton>
      }
    >
      {isExpandFilter && modalContent}
    </AppCard>
  );
};

FeedbackFilter.propTypes = {
  productFilter: PropTypes.string.isRequired,
  setProductFilter: PropTypes.func.isRequired,
  isFilterOpen: PropTypes.bool.isRequired,
  setIsFilterOpen: PropTypes.func.isRequired,
  activityFilter: PropTypes.string,
  setActivityFilter: PropTypes.func,
  dateFilter: PropTypes.string,
  setDateFilter: PropTypes.func,
  timeFilter: PropTypes.string,
  setTimeFilter: PropTypes.func,
  yearFilter: PropTypes.string,
  setYearFilter: PropTypes.func,
  years: PropTypes.array,
  setPage: PropTypes.func,
  isMobile: PropTypes.bool,
  openFilterModal: PropTypes.bool,
  handleFilterModalClose: PropTypes.func,
};

export default FeedbackFilter;
